/**
 * POS Print Renderer Utility
 * Generates dynamic HTML for POS receipt printing with configurable layout
 */

import {
  PosPrintConfig,
  defaultPosPrintConfig,
} from "../config/posPrintConfig";

export interface PosPrintPayload {
  store_name?: string;
  store_address?: string;
  store_tele?: string;
  store_logo?: string;
  store_inv_notes?: string;
  store_tin?: string;
  store_reg_no?: string;
  /** VAT/tax percentage applied to this invoice; null/undefined for a normal (non-tax) invoice. */
  tax_per?: number | null;
  customer_address?: string;
  customer_tin_no?: string;
  customer_phone?: string;
  invoice_number?: string;
  /** Overrides the printed document heading (defaults to "INVOICE"), e.g. "QUOTATION". */
  document_label?: string;
  /** Overrides the "INV No :" row label to match document_label, e.g. "Quotation No :". */
  invoice_number_label?: string;
  /** Hides the payment breakdown / change row block - used for documents with no collected payment, e.g. quotations. */
  hide_payment_section?: boolean;
  /** Overrides the "Grand total" row label, e.g. "Total" for quotations. */
  total_label?: string;
  /** Extra terms/notes shown at the footer, in addition to store_inv_notes, e.g. quotation validity terms. */
  footer_notes?: string;
  date_time?: string;
  date?: string;
  time?: string;
  cashier_id?: string;
  cashier_name?: string;
  customer_code?: string;
  customer_name?: string;
  transaction_type?: string;
  is_reprint?: boolean;
  payment_method?: string;
  payment_breakdown?: Array<{
    label: string;
    amount: number;
  }>;
  subtotal?: number;
  discount_label?: string;
  discount_percentage?: number;
  discount_amount?: number;
  promotion_discount_amount?: number;
  total_amount?: number;
  total_items?: number;
  total_quantity?: number;
  pay_mode?: string;
  paid_amount?: number;
  change_amount?: number;
  promotions?: Array<{
    promo_code?: string | null;
    promo_name?: string | null;
    promo_free_item?: string | null;
    promo_free_voucher?: string | null;
  }>;
  items: Array<{
    code?: string;
    name: string;
    quantity: number;
    price: number;
    discount_percentage?: number;
    /** Per-unit discount amount; set instead of discount_percentage, never both. */
    discount_amount?: number;
    total: number;
  }>;
}

export interface PosPrintRow {
  key: string;
  code?: string;
  name: string;
  quantity: number;
  price: number;
  total: number;
  discountPercentage: number;
  discountAmount: number;
}

export type PrintPaperSize = 'POS' | 'A4';

export interface PrintPosBillOptions {
  paperSize?: PrintPaperSize;
  onError?: (msg: string) => void;
  /** Runs once the print dialog has closed (or printing failed), e.g. to restore focus. */
  onAfterPrint?: () => void;
}

const A4_PRINT_CONFIG: PosPrintConfig = {
  ...defaultPosPrintConfig,
  page: {
    width: '21cm',
    height: '29.7cm',
  },
  layout: {
    left_margin: '1.5cm',
    right_margin: '1.5cm',
  },
  sections: {
    header: {
      top: '1.0cm',
      left: '1.5cm',
      height: '5.5cm',
    },
    table: {
      top: '6.2cm',
      left: '1.5cm',
      width: '18.0cm',
      row_height: '0.75cm',
    },
    footer: {
      top: '23.5cm',
      height: '3.8cm',
    },
  },
  tableMeta: {
    bottom_padding: 2.2,
    footer_height: 2.8,
  },
};

const resolvePrintConfig = (
  paperSize: PrintPaperSize,
  config?: PosPrintConfig
): PosPrintConfig => {
  const baseConfig = config || defaultPosPrintConfig;

  if (paperSize !== 'A4') {
    return baseConfig;
  }

  return {
    ...baseConfig,
    page: { ...(baseConfig.page || {}), ...A4_PRINT_CONFIG.page },
    layout: { ...(baseConfig.layout || {}), ...A4_PRINT_CONFIG.layout },
    sections: {
      header: { ...(baseConfig.sections?.header || {}), ...A4_PRINT_CONFIG.sections.header },
      table: { ...(baseConfig.sections?.table || {}), ...A4_PRINT_CONFIG.sections.table },
      footer: { ...(baseConfig.sections?.footer || {}), ...A4_PRINT_CONFIG.sections.footer },
    },
    tableMeta: { ...(baseConfig.tableMeta || {}), ...A4_PRINT_CONFIG.tableMeta },
  };
};

const cm = (v: string | number | undefined | null): string => {
  if (v === undefined || v === null || v === "") return "0cm";
  return String(v).includes("cm") ? String(v) : `${v}cm`;
};

const escapeHtml = (value: unknown): string => {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const NUMBER_ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const NUMBER_TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const NUMBER_SCALES = ['', 'Thousand', 'Million', 'Billion'];

const threeDigitsToWords = (num: number): string => {
  let str = '';
  if (num >= 100) {
    str += `${NUMBER_ONES[Math.floor(num / 100)]} Hundred`;
    num %= 100;
    if (num > 0) str += ' ';
  }
  if (num >= 20) {
    str += NUMBER_TENS[Math.floor(num / 10)];
    if (num % 10 > 0) str += `-${NUMBER_ONES[num % 10]}`;
  } else if (num > 0) {
    str += NUMBER_ONES[num];
  }
  return str;
};

const integerToWords = (num: number): string => {
  if (num === 0) return 'Zero';
  let scaleIndex = 0;
  const parts: string[] = [];
  let n = Math.floor(num);
  while (n > 0) {
    const chunk = n % 1000;
    if (chunk > 0) {
      const chunkWords = threeDigitsToWords(chunk);
      parts.unshift(NUMBER_SCALES[scaleIndex] ? `${chunkWords} ${NUMBER_SCALES[scaleIndex]}` : chunkWords);
    }
    n = Math.floor(n / 1000);
    scaleIndex += 1;
  }
  return parts.join(' ');
};

export const amountToWords = (amount: number, currency = 'Rupees', subCurrency = 'Cents'): string => {
  const value = Math.max(0, Number(amount) || 0);
  const rupees = Math.floor(value);
  const cents = Math.round((value - rupees) * 100);
  let result = `${integerToWords(rupees)} ${currency}`;
  if (cents > 0) {
    result += ` And ${integerToWords(cents)} ${subCurrency}`;
  }
  return `${result} Only`;
};

/** Formats a date as MM/DD/YYYY for consistent display across all printed invoices. */
export const formatInvoiceDate = (date: Date): string => {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const hasPrintableLogo = (value: string | undefined): boolean => {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized.startsWith("data:image/") || normalized.startsWith("http://") || normalized.startsWith("https://");
};

interface InvoicePrintContext {
  payload: PosPrintPayload;
  isA4: boolean;
  pageWidth: string;
  taxPer: number | null;
  computedSubtotal: number;
  hasPromotionRows: boolean;
}

const buildPrintContext = (
  payload: PosPrintPayload,
  config: PosPrintConfig | undefined,
  paperSize: PrintPaperSize
): { context: InvoicePrintContext; isTaxInvoice: boolean; rows: PosPrintRow[] } => {
  const finalConfig = resolvePrintConfig(paperSize, config);
  const isA4 = paperSize === 'A4';
  const hasPromotionRows = (payload.promotions || []).length > 0;

  const taxPer = payload.tax_per != null ? Number(payload.tax_per) : null;
  const isTaxInvoice = taxPer != null && taxPer > 0;

  const rows: PosPrintRow[] = (payload.items || []).map((item) => {
    const rawPrice = Number(item.price || 0);
    const quantity = item.quantity || 0;
    // Tax invoices print the item price net of VAT, and the line total as
    // (displayed price * qty), so the printed row math is self-consistent.
    // The saved/underlying amounts (item.total, invoice totals) are untouched.
    const displayPrice = isTaxInvoice && taxPer
      ? Number((rawPrice / (1 + taxPer / 100)).toFixed(2))
      : rawPrice;
    const displayTotal = isTaxInvoice && taxPer
      ? Number((displayPrice * quantity).toFixed(2))
      : Number(item.total || 0);

    return {
      key: `row-${Math.random().toString(36).substr(2, 9)}`,
      code: item.code,
      name: item.name || "",
      quantity,
      price: displayPrice,
      total: displayTotal,
      discountPercentage: Number(item.discount_percentage || 0),
      discountAmount: Number(item.discount_amount || 0),
    };
  });

  const computedSubtotal = isTaxInvoice
    ? Number(rows.reduce((sum, row) => sum + row.total, 0).toFixed(2))
    : Number(payload.subtotal || 0);

  const pageWidth = cm(finalConfig.page.width || (isA4 ? '21cm' : '8cm'));

  return {
    context: {
      payload,
      isA4,
      pageWidth,
      taxPer,
      computedSubtotal,
      hasPromotionRows,
    },
    isTaxInvoice,
    rows,
  };
};

const buildLogoHtml = (payload: PosPrintPayload, isA4: boolean): string =>
  hasPrintableLogo(payload.store_logo)
    ? `
      <div style="margin-bottom: 8px;">
        <img
          src="${escapeHtml(payload.store_logo)}"
          alt="${escapeHtml(payload.store_name || "Company logo")}"
          style="max-width: ${isA4 ? '220px' : '150px'}; max-height: ${isA4 ? '72px' : '52px'}; width: auto; height: auto; object-fit: contain;"
        />
      </div>
    `
    : `<div style="font-size: ${isA4 ? '24px' : '18px'}; font-weight: 700; margin-bottom: 4px; letter-spacing: 0.02em;">${escapeHtml(payload.store_name || "COMPANY NAME")}</div>`;

const buildReprintLabelHtml = (payload: PosPrintPayload, isA4: boolean): string =>
  payload.is_reprint
    ? `<div style="display: inline-block; margin-top: 4px; padding: 2px 10px; border: 1px solid #000; font-size: ${isA4 ? '13px' : '11px'}; font-weight: 800; letter-spacing: 0.08em;">REPRINT</div>`
    : "";

const buildCompanyDetailsBlockHtml = (payload: PosPrintPayload, isA4: boolean): string => `
  <div style="font-size: ${isA4 ? '13px' : '11px'}; margin-bottom: 2px; color: #374151;">${escapeHtml(payload.store_address || "Address")}</div>
  <div style="font-size: ${isA4 ? '13px' : '11px'}; margin-bottom: 2px; color: #374151;">Tel: ${escapeHtml(payload.store_tele || "-")}</div>
  ${payload.store_tin ? `<div style="font-size: ${isA4 ? '13px' : '11px'}; margin-bottom: 2px; color: #374151;">TIN No: ${escapeHtml(payload.store_tin)}</div>` : ''}
  ${payload.store_reg_no ? `<div style="font-size: ${isA4 ? '13px' : '11px'}; margin-bottom: 2px; color: #374151;">Reg No: ${escapeHtml(payload.store_reg_no)}</div>` : ''}
`;

const buildCustomerDetailsBlockHtml = (payload: PosPrintPayload, isA4: boolean): string => `
  <div style="font-size: ${isA4 ? '13px' : '11px'}; font-weight: 700; margin-bottom: 2px; color: #111827;">Customer Code: ${escapeHtml(payload.customer_code || '-')}</div>
  <div style="font-size: ${isA4 ? '13px' : '11px'}; margin-bottom: 2px; color: #374151;">Name: ${escapeHtml(payload.customer_name || 'Walk-in Customer')}</div>
  ${payload.customer_phone ? `<div style="font-size: ${isA4 ? '13px' : '11px'}; margin-bottom: 2px; color: #374151;">Tel: ${escapeHtml(payload.customer_phone)}</div>` : ''}
  ${payload.customer_address ? `<div style="font-size: ${isA4 ? '13px' : '11px'}; margin-bottom: 2px; color: #374151;">${escapeHtml(payload.customer_address)}</div>` : ''}
  ${payload.customer_tin_no ? `<div style="font-size: ${isA4 ? '13px' : '11px'}; margin-bottom: 2px; color: #374151;">TIN No: ${escapeHtml(payload.customer_tin_no)}</div>` : ''}
`;

// Tax invoices print the company name as a plain (bold) line above the
// address block instead of the large centered logo/name heading used by
// the standard invoice and other layouts.
const buildTaxInvoiceCompanyBlockHtml = (payload: PosPrintPayload, isA4: boolean): string => `
  <div style="font-size: ${isA4 ? '15px' : '13px'}; font-weight: 700; margin-bottom: 2px; color: #111827;">${escapeHtml(payload.store_name || "COMPANY NAME")}</div>
  ${buildCompanyDetailsBlockHtml(payload, isA4)}
`;

// Pagination is left to the browser: each item is its own <tbody> with
// break-inside: avoid so its two rows never split across pages, and on A4 the
// document header lives inside <thead> so it repeats on every printed page.
// POS rolls render the header above the table instead, so it prints once.
// The totals footer is the table's last unbreakable <tbody>, so when it moves
// to a new page that page still gets the repeated header.
const buildItemsTableHtml = (
  rows: PosPrintRow[],
  isA4: boolean,
  headerHtml: string,
  footerHtml: string,
  options?: { showRowNumber?: boolean; showDiscountColumns?: boolean; qtyBeforePrice?: boolean }
): string => {
  const showRowNumber = options?.showRowNumber ?? false;
  const showDiscountColumns = options?.showDiscountColumns ?? true;
  const qtyBeforePrice = options?.qtyBeforePrice ?? false;

  // Borders sit on cells (separate border model) rather than rows: Chrome
  // mis-resolves collapsed row borders on a <thead> repeated after a page break.
  const headCellStyle = 'padding: 3px 2px; border-top: 1px solid #000; border-bottom: 1px solid #000; vertical-align: bottom;';
  const itemRowEndStyle = 'padding: 0 2px 2px; border-bottom: 1px dotted #eee;';
  // Item name/code print solid black and bold so they stay legible on thermal paper.
  const itemNameStyle = `font-weight: 700; color: #000; font-size: ${isA4 ? '13px' : '11px'};`;
  const itemCodeStyle = `font-weight: 700; color: #000; font-size: ${isA4 ? '12px' : '10px'};`;

  type DataColumn = {
    header: string;
    align: 'left' | 'center' | 'right';
    // Relative width; the table is fixed-layout so these, not the content,
    // decide the column widths and nothing can push the table past the margins.
    weight: number;
    cell: (row: PosPrintRow) => string;
  };

  const priceColumn: DataColumn = { header: 'PRICE', align: 'right', weight: 18, cell: (row) => row.price.toFixed(2) };
  const qtyColumn: DataColumn = { header: 'QTY', align: 'center', weight: 11, cell: (row) => String(row.quantity) };
  // A line carries either a percentage or a per-unit amount discount, never both.
  const discountColumns: DataColumn[] = showDiscountColumns
    ? [
      { header: 'DIS%', align: 'right', weight: 11, cell: (row) => row.discountPercentage.toFixed(2) },
      { header: 'DIS AMT', align: 'right', weight: 16, cell: (row) => row.discountAmount.toFixed(2) },
    ]
    : [];

  const columns: DataColumn[] = [
    ...(showRowNumber ? [{ header: '#', align: 'center' as const, weight: 6, cell: () => '' }] : []),
    { header: 'ITEM', align: 'left', weight: showDiscountColumns ? 18 : 34, cell: (row) => `<span style="${itemCodeStyle}">${escapeHtml(row.code || '-')}</span>` },
    ...(qtyBeforePrice ? [qtyColumn, priceColumn] : [priceColumn, ...discountColumns, qtyColumn]),
    { header: 'TOTAL', align: 'right', weight: 20, cell: (row) => row.total.toFixed(2) },
  ];
  const totalColumns = columns.length;
  const nameRowColspan = totalColumns - (showRowNumber ? 1 : 0);
  const totalWeight = columns.reduce((sum, column) => sum + column.weight, 0);
  const colgroupHtml = columns
    .map((column) => `<col style="width: ${((column.weight / totalWeight) * 100).toFixed(3)}%;">`)
    .join('');
  const detailColumns = showRowNumber ? columns.slice(1) : columns;

  return `
    ${isA4 ? '' : headerHtml}
    <table style="width: 100%; max-width: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 0; overflow-wrap: anywhere; word-break: break-word; font-size: ${isA4 ? '12px' : '9px'}; font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;">
      <colgroup>${colgroupHtml}</colgroup>
      <thead style="display: table-header-group;">
        ${isA4 ? `<tr><td colspan="${totalColumns}" style="padding: 0;">${headerHtml}</td></tr>` : ''}
        <tr style="font-weight: 700;">
          ${columns.map((column) => `<th style="text-align: ${column.align}; ${headCellStyle}">${column.header}</th>`).join('')}
        </tr>
      </thead>
      ${rows
        .map(
          (row, rowIndex) => {
            // row.price/row.total are already tax-exclusive (and price*qty)
            // for tax invoices; saved/underlying invoice amounts are untouched.
            return `
              <tbody style="break-inside: avoid; page-break-inside: avoid;">
                <tr style="vertical-align: top; break-after: avoid; page-break-after: avoid;">
                  ${showRowNumber ? `<td rowspan="2" style="text-align: center; padding: 4px 2px 0; ${itemRowEndStyle}">${rowIndex + 1}</td>` : ''}
                  <td colspan="${nameRowColspan}" style="padding: 4px 2px 0; ${itemNameStyle}">${escapeHtml(row.name)}</td>
                </tr>
                <tr style="vertical-align: top;">
                  ${detailColumns.map((column) => `<td style="text-align: ${column.align}; ${itemRowEndStyle}">${column.cell(row)}</td>`).join('')}
                </tr>
              </tbody>
            `;
          }
        )
        .join("")}
      <tbody style="break-inside: avoid; page-break-inside: avoid;">
        <tr><td colspan="${totalColumns}" style="padding: 0;">${footerHtml}</td></tr>
      </tbody>
    </table>
  `;
};

const buildPaymentsAndPromotionsHtml = (ctx: InvoicePrintContext): string => {
  const { payload, isA4, hasPromotionRows } = ctx;
  return `
    ${(payload.payment_breakdown || []).map((payment) => `
      <div style="display: flex; justify-content: space-between; border-top: 1px solid #eee; padding-top: 4px;">
        <span>${escapeHtml(payment.label)}</span>
        <span>${Number(payment.amount || 0).toFixed(2)}</span>
      </div>
    `).join("")}
    <div style="display: flex; justify-content: space-between; font-weight: 700;">
      <span>Change</span>
      <span>${payload.change_amount?.toFixed(2) || "0.00"}</span>
    </div>
    ${hasPromotionRows ? `
      <div style="border-top: 1px solid #000; padding-top: 6px; margin-top: 6px; font-size: ${isA4 ? '11px' : '10px'}; line-height: 1.6;">
        ${(payload.promotions || []).map((promo) => `
          ${promo.promo_free_item ? `
            <div style="display: flex; justify-content: space-between;">
              <span>Free Item:</span>
              <span>${escapeHtml(String(promo.promo_free_item))}</span>
            </div>
          ` : ''}
          ${promo.promo_free_voucher ? `
            <div style="display: flex; justify-content: space-between;">
              <span>Free Voucher:</span>
              <span>${escapeHtml(String(promo.promo_free_voucher))}</span>
            </div>
          ` : ''}
        `).join("")}
      </div>
    ` : ''}
  `;
};

const isWalkInCustomer = (payload: PosPrintPayload): boolean => {
  const name = (payload.customer_name || "").trim().toLowerCase();
  return !name || name === "walk-in customer" || name === "walk in customer";
};

const buildCustomerSignatureHtml = (payload: PosPrintPayload, isA4: boolean): string => {
  if (!isA4 || isWalkInCustomer(payload)) return "";
  return `
    <div style="margin-top: 60px;">
      <div style="width: 220px; border-top: 1px solid #000; padding-top: 4px; font-size: 12px;">
        Customer Signature
      </div>
    </div>
  `;
};

// Margins are built into the content as well as @page: printer drivers and the
// "Margins: None" dialog option can drop @page margins, and a slightly shifted
// or narrower roll/sheet then clips the edges. Horizontal padding repeats on
// every printed page; box-decoration-break clones the vertical padding too.
const buildPageWrapper = (
  ctx: InvoicePrintContext,
  tableHtml: string
): string => `
  <div style="
    width: 100%;
    max-width: ${ctx.pageWidth};
    padding: ${ctx.isA4 ? '4mm 6mm' : '3mm 4mm'};
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    overflow-wrap: anywhere;
    background: white;
    color: #111827;
    font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  ">
    ${tableHtml}
  </div>
`;

// --- Standard (non-tax) invoice layout ---

const renderStandardInvoice = (
  rows: PosPrintRow[],
  ctx: InvoicePrintContext
): string => {
  const { payload, isA4 } = ctx;
  const documentLabel = payload.document_label || 'INVOICE';
  const invoiceNumberLabel = payload.invoice_number_label || 'INV No :';

  const headerHtml = `
    <div style="text-align: center; margin-bottom: 10px; border-bottom: 1px solid #000; padding-bottom: 5px;">
      ${buildLogoHtml(payload, isA4)}
      ${buildCompanyDetailsBlockHtml(payload, isA4)}
      <div style="font-size: ${isA4 ? '16px' : '13px'}; font-weight: 700; margin-top: 6px; border-top: 1px solid #000; padding-top: 4px; letter-spacing: 0.08em;">${escapeHtml(documentLabel)}</div>
      ${buildReprintLabelHtml(payload, isA4)}
    </div>

    <div style="font-size: ${isA4 ? '12px' : '11px'}; line-height: 1.5; margin-bottom: 8px;">
      <div style="display: flex;">
        <span style="width: ${isA4 ? '90px' : '70px'};">${escapeHtml(invoiceNumberLabel)}</span>
        <span>${escapeHtml(payload.invoice_number)}</span>
      </div>
      <div style="display: flex;">
        <span style="width: ${isA4 ? '90px' : '70px'};">Cashier :</span>
        <span>${escapeHtml(payload.cashier_id)}/${escapeHtml(payload.cashier_name)}</span>
      </div>
      <div style="display: flex;">
        <span style="width: ${isA4 ? '90px' : '70px'};">Customer :</span>
        <span>${escapeHtml(payload.customer_name)}</span>
      </div>
      <div style="display: flex; justify-content: space-between;">
        <div>
          <span style="width: 70px; display: inline-block;">Date :</span>
          <span>${escapeHtml(payload.date || payload.date_time?.split(',')[0])}</span>
        </div>
        <div>
          <span>Time:</span>
          <span>${escapeHtml(payload.time || payload.date_time?.split(',')[1])}</span>
        </div>
      </div>
    </div>
  `;

  const footerHtml = `
    <div style="margin-top: 8px; border-top: 1px solid #000; font-size: ${isA4 ? '12px' : '11px'}; line-height: 1.55;">
      <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #eee; padding: 2px 0;">
        <span>Total items : ${payload.total_items || payload.items.length}</span>
        <span>Total quantity: ${payload.total_quantity || payload.items.reduce((acc, i) => acc + i.quantity, 0)}</span>
      </div>
      ${payload.transaction_type && (payload.transaction_type.includes('Return') || payload.transaction_type.includes('return')) ? `<div style="text-align: center; padding: 4px 0; color: red; font-weight: bold;">** ${payload.transaction_type.toUpperCase()} **</div>` : ''}
      <div style="display: flex; justify-content: space-between; padding-top: 4px;">
        <span>Sub total</span>
        <span>${ctx.computedSubtotal.toFixed(2)}</span>
      </div>
      <div style="display: flex; justify-content: space-between;">
        <span>Bill discount%</span>
        <span>${payload.discount_percentage?.toFixed(2) || "0.00"}</span>
      </div>
      ${payload.promotion_discount_amount && payload.promotion_discount_amount > 0 ? `
      <div style="display: flex; justify-content: space-between;">
        <span>Promotion</span>
        <span>${Number(payload.promotion_discount_amount).toFixed(2)}</span>
      </div>
      ` : ''}
      <div style="display: flex; justify-content: space-between; font-weight: 700; font-size: ${isA4 ? '16px' : '14px'}; margin: 4px 0; border-top: 1px solid #000; padding-top: 2px;">
        <span>${escapeHtml(payload.total_label || 'Grand total')}</span>
        <span>${payload.total_amount?.toFixed(2) || "0.00"}</span>
      </div>
      ${payload.hide_payment_section ? '' : buildPaymentsAndPromotionsHtml(ctx)}
      ${buildCustomerSignatureHtml(payload, isA4)}
      <div style="text-align: center; margin-top: 15px; border-top: 1px solid #000; padding-top: 5px; font-weight: 700;">
      ${payload.footer_notes ? `
        <div style="text-align: left; margin-top: 6px; font-size: ${isA4 ? '11px' : '10px'}; font-weight: 400; white-space: pre-line;">${escapeHtml(payload.footer_notes)}</div>
      ` : ''}
        Thank you!
      </div>

      ${payload.store_inv_notes ? `
        <div style="text-align: center; margin-top: 6px; font-size: 11px; font-weight: 400; font-style: italic;">${escapeHtml(payload.store_inv_notes)}</div>
      ` : ''}
    </div>
  `;

  const tableHtml = buildItemsTableHtml(rows, isA4, headerHtml, footerHtml, {
    showRowNumber: true,
  });

  return buildPageWrapper(ctx, tableHtml);
};

// --- Tax invoice layout ---

const renderTaxInvoice = (
  rows: PosPrintRow[],
  ctx: InvoicePrintContext
): string => {
  const { payload, isA4, taxPer } = ctx;

  const reprintBlockHtml = payload.is_reprint
    ? `<div style="margin-top: 6px;">${buildReprintLabelHtml(payload, isA4)}</div>`
    : '';

  const headerHtml = `
    <div style="text-align: center; margin-bottom: 10px; border-bottom: 1px solid #000; padding-bottom: 5px;">
      <div style="display: inline-block; background-color: #e5e7eb; -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 4px 14px; font-size: ${isA4 ? '16px' : '13px'}; font-weight: 700; letter-spacing: 0.08em;">TAX INVOICE</div>
      ${reprintBlockHtml}
    </div>

    <div style="display: flex; justify-content: space-between; gap: 16px; text-align: left; margin-bottom: 8px;">
      <div style="flex: 1;">${buildTaxInvoiceCompanyBlockHtml(payload, isA4)}</div>
      <div style="flex: 1;">${buildCustomerDetailsBlockHtml(payload, isA4)}</div>
    </div>

    <div style="display: flex; justify-content: space-between; gap: 16px; text-align: left; font-size: ${isA4 ? '12px' : '11px'}; line-height: 1.5; margin-bottom: 8px;">
      <div style="flex: 1;">
        <div>
          <span>Date : </span>
          <span>${escapeHtml(payload.date || payload.date_time?.split(',')[0])}</span>
        </div>
        <div>
          <span>Cashier : </span>
          <span>${escapeHtml(payload.cashier_id)}/${escapeHtml(payload.cashier_name)}</span>
        </div>
      </div>
      <div style="flex: 1;">
        <div>
          <span>INV No : </span>
          <span>${escapeHtml(payload.invoice_number)}</span>
        </div>
        <div>
          <span>Time:</span>
          <span>${escapeHtml(payload.time || payload.date_time?.split(',')[1])}</span>
        </div>
      </div>
    </div>
  `;

  const footerHtml = `
    <div style="margin-top: 8px; border-top: 1px solid #000; font-size: ${isA4 ? '12px' : '11px'}; line-height: 1.55;">
      <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #eee; padding: 2px 0;">
        <span>Total items : ${payload.total_items || payload.items.length}</span>
        <span>Total quantity: ${payload.total_quantity || payload.items.reduce((acc, i) => acc + i.quantity, 0)}</span>
      </div>
      ${payload.transaction_type && (payload.transaction_type.includes('Return') || payload.transaction_type.includes('return')) ? `<div style="text-align: center; padding: 4px 0; color: red; font-weight: bold;">** ${payload.transaction_type.toUpperCase()} **</div>` : ''}
      <div style="display: flex; justify-content: space-between; padding-top: 4px;">
        <span>Sub total</span>
        <span>${ctx.computedSubtotal.toFixed(2)}</span>
      </div>
      <div style="display: flex; justify-content: space-between;">
        <span>VAT (${Number(taxPer).toFixed(2)}%)</span>
        <span>${(ctx.computedSubtotal * Number(taxPer) / 100).toFixed(2)}</span>
      </div>
      ${payload.promotion_discount_amount && payload.promotion_discount_amount > 0 ? `
      <div style="display: flex; justify-content: space-between;">
        <span>Promotion</span>
        <span>${Number(payload.promotion_discount_amount).toFixed(2)}</span>
      </div>
      ` : ''}
      <div style="display: flex; justify-content: space-between; font-weight: 700; font-size: ${isA4 ? '16px' : '14px'}; margin: 4px 0; border-top: 1px solid #000; padding-top: 2px;">
        <span>Grand total</span>
        <span>${payload.total_amount?.toFixed(2) || "0.00"}</span>
      </div>
      ${buildPaymentsAndPromotionsHtml(ctx)}
      <div style="margin-top: 10px; border-top: 1px solid #000; padding-top: 6px; font-size: ${isA4 ? '12px' : '11px'};">
        <span style="font-weight: 700;">Amount in words:</span>
        <span style="font-style: italic;"> ${escapeHtml(amountToWords(Number(payload.total_amount || 0)))}</span>
      </div>
      ${buildCustomerSignatureHtml(payload, isA4)}
      <div style="text-align: center; margin-top: 15px; border-top: 1px solid #000; padding-top: 5px; font-weight: 700;">
        Thank you!
      </div>
      ${payload.store_inv_notes ? `
        <div style="text-align: center; margin-top: 6px; font-size: 11px; font-weight: 400; font-style: italic;">${escapeHtml(payload.store_inv_notes)}</div>
      ` : ''}
    </div>
  `;

  const tableHtml = buildItemsTableHtml(rows, isA4, headerHtml, footerHtml, {
    showRowNumber: true,
    showDiscountColumns: false,
    qtyBeforePrice: true,
  });

  return buildPageWrapper(ctx, tableHtml);
};

export const generatePosPrintHTML = (
  payload: PosPrintPayload,
  config?: PosPrintConfig,
  paperSize: PrintPaperSize = 'POS'
): string => {
  const { context, isTaxInvoice, rows } = buildPrintContext(payload, config, paperSize);

  return isTaxInvoice
    ? renderTaxInvoice(rows, context)
    : renderStandardInvoice(rows, context);
};

const PRINT_FRAME_ID = "pos-print-frame";

export const printPosBill = (
  payload: PosPrintPayload,
  config?: PosPrintConfig,
  options?: PrintPosBillOptions
) => {
  const paperSize = options?.paperSize || 'POS';
  const finalConfig = resolvePrintConfig(paperSize, config);
  const pageWidth = cm(finalConfig.page.width || (paperSize === 'A4' ? '21cm' : '8cm'));
  const html = generatePosPrintHTML(payload, finalConfig, paperSize);

  const styles = `
    <style>
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
      @page {
        margin: ${paperSize === 'A4' ? '8mm' : '0'};
        size: ${paperSize === 'A4' ? 'A4' : `${pageWidth} auto`};
      }
      body {
        font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
        background: #f5f5f5;
        padding: 10px;
        color: #111827;
      }
      @media print {
        body {
          background: white;
          padding: 0;
        }
        .no-print {
          display: none;
        }
        .receipt-wrapper {
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }
      }
    </style>
  `;

  const documentHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Receipt</title>
        ${styles}
      </head>
      <body>
        <div class="receipt-wrapper">
          ${html}
        </div>
      </body>
    </html>
  `;

  // Print from an off-screen iframe instead of a popup: no extra window to
  // open/close, and no popup blocker. The frame is its own document, so the
  // @page rules above still decide POS vs A4 sizing. It keeps a real size at
  // the paper width (display:none / 0x0 frames can print blank).
  document.getElementById(PRINT_FRAME_ID)?.remove();

  const frame = document.createElement("iframe");
  frame.id = PRINT_FRAME_ID;
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  Object.assign(frame.style, {
    position: "fixed",
    left: "-10000px",
    top: "0",
    width: pageWidth,
    height: "600px",
    border: "0",
  });

  // srcdoc's load event fires only after the logo/images have loaded.
  frame.onload = () => {
    const frameWindow = frame.contentWindow;
    if (!frameWindow) {
      frame.remove();
      options?.onError?.("Failed to prepare the print document.");
      options?.onAfterPrint?.();
      return;
    }

    let removed = false;
    const removeFrame = () => {
      if (removed) return;
      removed = true;
      // Defer so the browser has finished with the document before it goes.
      setTimeout(() => {
        frame.remove();
        options?.onAfterPrint?.();
      }, 0);
    };
    frameWindow.addEventListener("afterprint", removeFrame);

    const fontsReady = frameWindow.document.fonts?.ready ?? Promise.resolve();
    fontsReady.then(() => {
      try {
        frameWindow.focus();
        // Blocks until the dialog closes in Edge/Chrome; afterprint covers
        // browsers where it doesn't.
        frameWindow.print();
        // The frame took focus to print; hand it back once the dialog is done.
        options?.onAfterPrint?.();
      } catch {
        removeFrame();
        options?.onError?.("Failed to open the print dialog.");
      }
    });
  };

  frame.srcdoc = documentHtml;
  document.body.appendChild(frame);
};
